/**
 * Funnel Stages - Conversion tracking constants
 *
 * Defines the hiring funnel stages, their order, and utilities
 * for tracking per-job progress from application to offer.
 */

export const FUNNEL_STAGES = [
  { key: 'applied',           label: 'Applied',          color: '#3b82f6', icon: '📤', order: 1 },
  { key: 'response_received', label: 'Response',         color: '#8b5cf6', icon: '📩', order: 2 },
  { key: 'phone_screen',      label: 'Phone Screen',     color: '#a855f7', icon: '📞', order: 3 },
  { key: 'interview',         label: 'Interview',        color: '#f59e0b', icon: '🎤', order: 4 },
  { key: 'final_round',       label: 'Final Round',      color: '#f97316', icon: '🏆', order: 5 },
  { key: 'offer',             label: 'Offer',            color: '#22c55e', icon: '🎉', order: 6 },
];

export const TERMINAL_STATUSES = ['rejected', 'withdrawn'];

// Pre-application statuses (not part of funnel)
export const PRE_APPLICATION_STATUSES = ['analyzing', 'ready', 'deferred', 'questions'];

/**
 * Map a job status to its funnel order (0 = pre-application, 1-6 = funnel stage)
 */
export function statusToFunnelOrder(status) {
  if (!status) return 0;
  const stage = FUNNEL_STAGES.find(s => s.key === status);
  if (stage) return stage.order;
  if (TERMINAL_STATUSES.includes(status)) return -1;
  return 0; // pre-application
}

/**
 * Get the stage object by key
 */
export function getStage(key) {
  return FUNNEL_STAGES.find(s => s.key === key) || null;
}

/**
 * Get valid next stages for a given status
 * (can advance forward, or mark as rejected/withdrawn from any stage)
 */
export function getValidNextStatuses(currentStatus) {
  const currentOrder = statusToFunnelOrder(currentStatus);
  if (currentOrder < 0) return []; // terminal
  if (currentOrder === 0) {
    // Pre-application: can only move to 'applied'
    return ['applied', 'withdrawn'];
  }
  // Can advance to any later stage, or go terminal
  const nextStages = FUNNEL_STAGES
    .filter(s => s.order > currentOrder)
    .map(s => s.key);
  return [...nextStages, 'rejected', 'withdrawn'];
}

/**
 * Get label + color for any status (including terminal + pre-application)
 */
export function getStatusDisplay(status) {
  const stage = FUNNEL_STAGES.find(s => s.key === status);
  if (stage) return { label: stage.label, color: stage.color, icon: stage.icon };
  switch (status) {
    case 'rejected':  return { label: 'Rejected',  color: '#ef4444', icon: '✗' };
    case 'withdrawn': return { label: 'Withdrawn', color: '#6b7280', icon: '↩' };
    case 'analyzing': return { label: 'Analyzing', color: '#9ca3af', icon: '⏳' };
    case 'ready':     return { label: 'Ready',     color: '#3b82f6', icon: '✓' };
    case 'deferred':  return { label: 'Deferred',  color: '#f59e0b', icon: '📌' };
    case 'questions': return { label: 'Questions', color: '#8b5cf6', icon: '?' };
    default:          return { label: status || 'Unknown', color: '#9ca3af', icon: '·' };
  }
}
